import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PhoneCall } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { useBrand } from "../../context/BrandContext";
import { Card, PageTitle, Btn, fmtDateTime } from "../../components/admin/AdminUI";
import { Bubble } from "../../components/chat/ChatMessages";

export function ChatList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get("/admin/chats", { params: { page } }).then((r) => setData(r.data)); }, [page]);
  return (
    <div data-testid="admin-chats">
      <PageTitle eyebrow="Chat" title="Chat transcripts" />
      <Card>
        {!data ? <p className="text-sm text-slate-500">Loading…</p> : data.items.length === 0 ? <p className="text-sm text-slate-500" data-testid="chats-empty">No conversations yet.</p> : (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm min-w-[680px]" data-testid="chats-table">
              <thead><tr className="text-left font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
                {["Last activity", "Last customer message", "Messages", "Lead"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.session_id} onClick={() => navigate(`/admin/chats/${s.session_id}`)} className="border-b border-slate-100 hover:bg-amber-50/50 cursor-pointer" data-testid={`chat-row-${s.session_id}`}>
                    <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(s.updated_at)}</td>
                    <td className="px-5 py-3 text-slate-800 max-w-md truncate">{s.last_user_message || "—"}</td>
                    <td className="px-5 py-3 text-slate-700">{s.message_count || 0}</td>
                    <td className="px-5 py-3">{s.has_lead && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5"><PhoneCall className="w-3 h-3" />Callback</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
            <span>{data.total} conversations · page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="chats-prev">Prev</Btn>
              <Btn variant="ghost" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="chats-next">Next</Btn>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export function ChatTranscript() {
  const b = useBrand();
  const { sid } = useParams();
  const [s, setS] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { adminApi.get(`/admin/chats/${sid}`).then((r) => setS(r.data)).catch(() => setMissing(true)); }, [sid]);
  if (missing) return <p className="text-slate-600" data-testid="chat-transcript-missing">Conversation not found.</p>;
  if (!s) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-chat-transcript">
      <Link to="/admin/chats" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-600 mb-4"><ArrowLeft className="w-4 h-4" />Chats</Link>
      <PageTitle eyebrow={`Session ${sid.slice(0, 8)}…`} title="Transcript" />
      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Conversation" className="lg:col-span-2">
          <div className="space-y-3 bg-[#F7F8FA] p-4 -m-5" data-testid="transcript-messages">
            {s.messages.map((m, i) => (
              <div key={i}>
                <div className={`text-[10px] font-mono text-slate-500 mb-1 ${m.role === "user" ? "text-right" : ""}`}>{m.role === "user" ? "Customer" : "Assistant"} · {fmtDateTime(m.at)}</div>
                <Bubble m={m} b={b} index={i} />
              </div>
            ))}
          </div>
        </Card>
        <Card title="Callback requests">
          {s.leads.length === 0 ? <p className="text-sm text-slate-500">None.</p> : s.leads.map((l) => (
            <div key={l.id} className="text-sm border-b border-slate-100 last:border-0 py-2" data-testid={`transcript-lead-${l.id}`}>
              <div className="font-semibold text-slate-900">{l.name}</div>
              <a href={`tel:${l.phone}`} className="text-brand-600">{l.phone}</a>{l.email && <span className="text-slate-600"> · {l.email}</span>}
              {l.question && <p className="mt-1 text-slate-700">{l.question}</p>}
              <p className="text-xs text-slate-500 mt-1">{fmtDateTime(l.created_at)}</p>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
